import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import Text, {
  type ResponsiveTextToken,
  type TextLeading,
  type TextToken,
} from '../base/TextNew';

type SegmentedControlToken = TextToken | ResponsiveTextToken;

export interface SegmentedControlOption {
  label: string;
  mobileLabel?: string;
  value: string;
}

interface SegmentedControlProps {
  options: SegmentedControlOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  token?: SegmentedControlToken;
  leading?: TextLeading;
  className?: string;
  buttonClassName?: string;
  stretch?: boolean;
  ariaLabel?: string;
}

export function SegmentedControl({
  options,
  value,
  defaultValue,
  onValueChange,
  token = '16/medium',
  leading,
  className = '',
  buttonClassName = '',
  stretch = false,
  ariaLabel,
}: SegmentedControlProps) {
  const [internalValue, setInternalValue] = useState(
    defaultValue ?? options[0]?.value ?? '',
  );
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [highlight, setHighlight] = useState({ left: 0, width: 0 });
  const hasMeasured = useRef(false);
  const activeValue = value ?? internalValue;

  const updateHighlight = useCallback(() => {
    const activeIndex = options.findIndex(
      (option) => option.value === activeValue,
    );
    const activeButton =
      buttonRefs.current[activeIndex >= 0 ? activeIndex : 0] ?? null;

    if (activeButton) {
      setHighlight({
        left: activeButton.offsetLeft,
        width: activeButton.offsetWidth,
      });
      hasMeasured.current = true;
    }
  }, [activeValue, options]);

  useLayoutEffect(() => {
    updateHighlight();
  }, [updateHighlight]);

  useEffect(() => {
    if (
      value === undefined &&
      options.length > 0 &&
      !options.some((option) => option.value === internalValue)
    ) {
      setInternalValue(defaultValue ?? options[0].value);
    }
  }, [defaultValue, internalValue, options, value]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateHighlight();
    });

    const container = containerRef.current;
    if (container) {
      observer.observe(container);
    }

    buttonRefs.current.forEach((button) => {
      if (button) {
        observer.observe(button);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [options, updateHighlight]);

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      aria-label={ariaLabel}
      className={`bg-gray-30 border-gray-30 relative flex w-fit flex-row flex-nowrap items-center rounded-[12px] border-4 ${className}`}
    >
      <div
        className={`absolute top-0 z-0 h-full rounded-lg bg-white ${
          hasMeasured.current ? 'transition-all duration-200' : ''
        }`}
        style={{
          left: highlight.left,
          width: highlight.width,
        }}
      />
      {options.map((option, index) => {
        const isActive = activeValue === option.value;

        return (
          <button
            key={option.value}
            ref={(button) => {
              buttonRefs.current[index] = button;
            }}
            type="button"
            className={`z-10 flex items-center justify-center px-3 py-1 ${stretch ? 'flex-1' : ''} ${buttonClassName}`}
            onClick={() => {
              if (value === undefined) {
                setInternalValue(option.value);
              }

              if (option.value !== activeValue) {
                onValueChange?.(option.value);
              }
            }}
          >
            <SegmentedControlLabel
              active={isActive}
              option={option}
              token={token}
              leading={leading}
            />
          </button>
        );
      })}
    </div>
  );
}

interface SegmentedControlLabelProps {
  active: boolean;
  option: SegmentedControlOption;
  token: SegmentedControlToken;
  leading?: TextLeading;
}

function SegmentedControlLabel({
  active,
  option,
  token,
  leading,
}: SegmentedControlLabelProps) {
  return (
    <Text
      as="span"
      token={token}
      leading={leading}
      className={`whitespace-nowrap text-center transition-colors ${
        active ? 'text-gray-90' : 'text-gray-60'
      }`}
    >
      {option.mobileLabel ? (
        <>
          <span className="md:hidden">{option.mobileLabel}</span>
          <span className="hidden md:inline">{option.label}</span>
        </>
      ) : (
        option.label
      )}
    </Text>
  );
}

interface SwitchProps {
  left: string;
  right: string;
  token?: SegmentedControlToken;
  leading?: TextLeading;
  className?: string;
  defaultValue?: boolean;
  superscript?: null | string;
  onChange?: (checked: boolean) => void;
}

export default function Switch({
  left,
  right,
  token = '16/medium',
  leading,
  className = '',
  defaultValue = false,
  superscript,
  onChange,
}: SwitchProps) {
  const [active, setActive] = useState(defaultValue);

  useEffect(() => {
    onChange?.(active);
  }, [active, onChange]);

  return (
    <div className="relative inline-flex">
      <SegmentedControl
        ariaLabel={`${left} or ${right}`}
        options={[
          { label: left, value: 'left' },
          { label: right, value: 'right' },
        ]}
        value={active ? 'right' : 'left'}
        onValueChange={(nextValue) => {
          setActive(nextValue === 'right');
        }}
        token={token}
        leading={leading}
        className={`mr-4 ${className}`}
      />
      {superscript && (
        <Text
          as="div"
          token="12"
          className={`${
            active ? 'bg-blue' : 'bg-gray-45'
          } pointer-events-none absolute right-0 top-0 z-10 -mr-4 -mt-4 rounded-full px-3 py-1 text-white transition-colors`}
        >
          {superscript}
        </Text>
      )}
    </div>
  );
}
